import { PrimeIndemnite } from '../types';
import { SALARY_CALCULATION } from './salary';
import { GENERATED_PRIMES } from '../data/generatedPrimes';

// Constants for prime calculation
export const PRIME_CALCULATION = {
  // Valeur de référence pour tous les calculs de primes
  // Traitement indiciaire brut annuel de l'indice majoré 100
  TRAITEMENT_IM100_ANNUEL: SALARY_CALCULATION.TRAITEMENT_INDICIAIRE_IM100_ANNUEL, // 5 907,34 €
  TRAITEMENT_IM100_MENSUEL: SALARY_CALCULATION.TRAITEMENT_INDICIAIRE_IM100_MENSUEL, // 492,28 €
  
  // Facteurs de calcul pour les primes
  FACTEUR_CALCUL_MENSUEL: 1200, // Diviseur standard pour les calculs mensuels
  
  // PSS (Prime Spéciale de Sujétion)
  PSS_TAUX: 0.10, // 10% du traitement indiciaire
  
  // ISS (Indemnité de Sujétion Spéciale - Prime des 13 heures)
  ISS_NUMERATEUR: 13,
  ISS_DENOMINATEUR: 1900,
  
  // Primes fixes
  SEGUR_FULL_TIME_AMOUNT: 238,
  SERVICE_DEFAULT_AMOUNT: 100,
  DEBUT_CARRIERE_AMOUNT: 38.35,
  VEIL_AMOUNT: 90,
  
  // Prime d'exercice en soins critiques
  SOINS_CRITIQUES_AMOUNT: 118,
  
  // Transport
  TRANSPORT_DEFAULT_RATE: 75, // 75% de remboursement
  TRANSPORT_DEFAULT_BASE: 40, // 40€ de base mensuelle
  TRANSPORT_DEFAULT_AMOUNT: 30, // 40€ × 75% = 30€
  
  // Autres primes de service
  SERVICE_MAX_AMOUNT: 200, // Montant maximum de la prime de service
  
  // Indemnité de résidence (IR)
  IR_ZONE_1_TAUX: 0.03, // 3% du traitement indiciaire
  IR_ZONE_2_TAUX: 0.01, // 1% du traitement indiciaire
  IR_ZONE_3_TAUX: 0.00, // 0% du traitement indiciaire
  
  // IFTD (Indemnité de Travail des Dimanches et Jours Fériés)
  IFTD_MONTANT_FORFAITAIRE: 60, // 60€ par jour travaillé (8h)
  IFTD_JOURS_FERIES_TOTAL: 11, // Nombre total de jours fériés en France
  
  // Prime de contagion
  CONTAGION_MONTANT_DEMI_JOURNEE: 0.33, // 0,33€ par demi-journée
  CONTAGION_JOURS_TRAVAILLES_DEFAUT: 228, // 228 jours travaillés par an par défaut
  
  // Prime de nuit
  PRIME_NUIT_MAJORATION: 0.25, // 25% de majoration
  PRIME_NUIT_NUITS_DEFAUT: 0, // 0 nuits par défaut
  
  // Supplément Familial de Traitement (SFT)
  SFT_IM_PLANCHER: 454, // Indice majoré plancher
  SFT_IM_PLAFOND: 722, // Indice majoré plafond
  SFT_PARTS_FIXES: {
    1: 2.29,
    2: 10.67,
    3: 15.24,
    ENFANT_SUPPLEMENTAIRE: 4.57
  },
  SFT_PARTS_PROPORTIONNELLES: {
    1: 0.00, // 0%
    2: 0.03, // 3%
    3: 0.08, // 8%
    ENFANT_SUPPLEMENTAIRE: 0.06 // 6%
  }
} as const;

// Primes that should be prorated based on work time
export const PRORATED_PRIMES = ['segur', 'service', 'sujetion', 'pss', 'veil'] as const;

// External links for official documentation
// Note: Ces URLs sont maintenant stockées dans Supabase (colonne url_doc)
// et ne sont utilisées que comme fallback pour les données de secours
export const PRIME_DOCUMENTATION_LINKS = {
  SEGUR: 'https://solidarites.gouv.fr/faq-sur-lextension-du-segur-dans-le-secteur-sanitaire-social-et-medico-social',
  TRANSPORT: 'https://www.fonction-publique.gouv.fr/toutes-les-actualites/la-prise-en-charge-des-titres-de-transport-des-agents-passe-de-50-75',
  PSS: 'https://www.fonction-publique.gouv.fr/etre-agent-public/ma-remuneration/le-regime-indemnitaire-fixe/la-prime-speciale-de-sujetion-pss',
  ISS: 'https://www.emploi-collectivites.fr/iss-treize-heures-indemnite-blog-territorial',
  DEBUT_CARRIERE: 'https://www.legifrance.gouv.fr/loda/id/JORFTEXT000000223257',
  INDEMNITE_RESIDENCE: 'https://www.service-public.fr/particuliers/vosdroits/F32511',
  ISG_DOM: 'https://www.emploi-collectivites.fr/sujetion-geographique-isg-dom-tom-blog-territorial',
  CONTAGION: 'https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000047260256',
  PRIME_NUIT: 'https://sante.gouv.fr/fichiers/bo/2023/2023.22.sante.pdf',
  VEIL: 'https://www.emploi-collectivites.fr/prime-infirmiere-specifique-veil-blog-territorial',
  SOINS_CRITIQUES: 'https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000044901814',
  SFT: 'https://outils.cisirh.gouv.fr/calculatrice-sft/form',
  IFTD: 'https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000048752777'
} as const;

/**
 * Default fallback primes data - automatically generated from Supabase
 * 
 * This data is generated by running `npm run generate-primes` which fetches
 * the latest data from Supabase and creates a synchronized fallback.
 * 
 * DO NOT EDIT MANUALLY - use the generate-primes script instead
 */
export const DEFAULT_PRIMES: PrimeIndemnite[] = GENERATED_PRIMES;